import React, {
  HTMLAttributes,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { AppointmentCancellationReason } from "appointment-experience-client";
import {
  AppointmentTask,
  AppointmentTaskStatus,
} from "../../constants/AppointmentOverlayTasks";
import { JourneyOverview } from "../../constants/AppointmentOverlayJourney";
import {
  JourneyStepChooser,
  JourneyFormRows,
  JourneyStepSwitch,
  useJourneyUpNext,
} from "./AppointmentOverlayJourney";
import AppointmentOverlayTaskRow from "./AppointmentOverlayTaskRow";
import type { AppointmentConnectivity } from "../../store/slices/appointmentOverlaySlice";
import type { AppointmentOutcome } from "./AppointmentOverlay";
import AppointmentOverlayConnectivity from "./AppointmentOverlayConnectivity";
import AppointmentOverlayVerification from "./AppointmentOverlayVerification";
import AppointmentOverlayExitChooser from "./AppointmentOverlayExitChooser";
import type { OverlaySide } from "./useOverlayDock";

type AppointmentOverlayCardProps = {
  patientName: string;
  /** The appointment's booked length, in minutes. */
  scheduledMinutes: number | null;
  /** Route to the patient profile, or null if the appointment has no patient. */
  patientPath: string | null;
  /** Route back to the appointment; the header eyebrow links to it. */
  detailsPath: string | null;
  patientInfoPath: string | null;
  patientVerified: boolean;
  onVerifyPatient: () => void;
  appointmentId: string | null;
  connectivity: AppointmentConnectivity | null;
  onConnectivityError: (message: string) => void;
  tasks: AppointmentTask[];
  /** The patient's care journey; its up-next step's forms count toward the ring. */
  journey: JourneyOverview | null;
  /** The focus chosen for this visit (null follows the suggestion), and setting it. */
  journeyStepId: string | null;
  onChooseJourneyStep: (stepId: string | null) => void;
  onOpenJourneyForm: (to: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Bottom corner the card is docked in. */
  side: OverlaySide;
  /** True while the card is being dragged by its grip. */
  dragging: boolean;
  cardRef: MutableRefObject<HTMLElement | null>;
  /** Pointer + keyboard handlers for the drag grip (see useOverlayDock). */
  gripProps: Pick<
    HTMLAttributes<HTMLElement>,
    "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel" | "onKeyDown"
  >;
  onTaskAction: (task: AppointmentTask) => void;
  onFinish: () => void;
  onNoShow: () => void;
  onExit: () => void;
  onCancelAppointment: () => void;
  /** Reasons to choose from when cancelling; null until they've been asked for. */
  cancellationReasons: AppointmentCancellationReason[] | null;
  cancelReason: string;
  onCancelReasonChange: (reasonId: string) => void;
  /** Set once an outcome has been chosen and the footer is asking to confirm. */
  pendingOutcome: AppointmentOutcome | null;
  onCancelFinish: () => void;
  /** An outcome that couldn't be saved; the footer offers Try again or Close anyway. */
  failedOutcome: AppointmentOutcome | null;
  onRetryOutcome: () => void;
  onForceClose: () => void;
  onConfirmFinish: () => void;
  /** True while the completion request is in flight. */
  finishing: boolean;
  /** The checklist's first load is still under way: placeholders instead of rows. */
  loading?: boolean;
  /** How many placeholders to show while loading: the rows last shown, else 2. */
  placeholderCount?: number;
  /** Told how many rows the loaded checklist shows, so a refresh can match it. */
  onChecklistRowCount?: (count: number) => void;
};

const AppointmentOverlayCard = ({
  patientName,
  scheduledMinutes,
  patientPath,
  detailsPath,
  patientInfoPath,
  patientVerified,
  onVerifyPatient,
  appointmentId,
  connectivity,
  onConnectivityError,
  tasks,
  journey,
  journeyStepId,
  onChooseJourneyStep,
  onOpenJourneyForm,
  collapsed,
  onToggleCollapsed,
  side,
  dragging,
  cardRef,
  gripProps,
  onTaskAction,
  onFinish,
  onNoShow,
  onExit,
  onCancelAppointment,
  cancellationReasons,
  cancelReason,
  onCancelReasonChange,
  pendingOutcome,
  onCancelFinish,
  failedOutcome,
  onRetryOutcome,
  onForceClose,
  onConfirmFinish,
  finishing,
  loading = false,
  placeholderCount = 2,
  onChecklistRowCount,
}: AppointmentOverlayCardProps) => {
  // The appointment's booked length, as scheduled.
  const timeLabel =
    typeof scheduledMinutes === "number" ? `${scheduledMinutes} MIN` : null;

  // Choosing a journey step takes over everything below the header.
  const [choosingJourneyStep, setChoosingJourneyStep] = useState(false);
  const { upNext, choose } = useJourneyUpNext(journey, journeyStepId, onChooseJourneyStep);
  const showJourney =
    !!journey && journey.steps.length + (journey.extras?.length ?? 0) > 0;
  const isChoosingJourneyStep = choosingJourneyStep && showJourney;

  const [choosingExit, setChoosingExit] = useState(false);
  const exitOutcome =
    pendingOutcome && pendingOutcome !== "completed" ? pendingOutcome : null;
  const isExiting = (choosingExit || !!exitOutcome) && !failedOutcome;
  const bodyCovered = isChoosingJourneyStep || isExiting;

  // The chooser is a drawer over the card's body: while it's open the body
  // behind it is inert, focus moves into the drawer, and closing returns focus
  // to "Change focus". Focus without scrolling, so the browser doesn't scroll
  // the card while it's still growing to fit the chooser.
  const panesRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasChoosing = useRef(false);
  useEffect(() => {
    const pane = paneRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (pane) pane.inert = isChoosingJourneyStep || isExiting;
    if (isChoosingJourneyStep !== wasChoosing.current) {
      fitPanesToDrawer(panesRef.current, pane, drawerRef.current, isChoosingJourneyStep);
    }
    if (isChoosingJourneyStep) {
      drawerRef.current
        ?.querySelector<HTMLButtonElement>("button")
        ?.focus({ preventScroll: true });
    } else if (wasChoosing.current) {
      pane
        ?.querySelector<HTMLButtonElement>(".appointment-overlay__journey-link")
        ?.focus({ preventScroll: true });
    }
    wasChoosing.current = isChoosingJourneyStep;
  }, [isChoosingJourneyStep]);

  const exitDrawerRef = useRef<HTMLDivElement>(null);
  const wasExiting = useRef(false);
  // Reruns when the cancellation reasons arrive, to fit them and focus the first.
  useEffect(() => {
    const pane = paneRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (pane) pane.inert = isChoosingJourneyStep || isExiting;
    if (isExiting || wasExiting.current) {
      fitPanesToDrawer(panesRef.current, pane, exitDrawerRef.current, isExiting);
    }
    if (isExiting) {
      exitDrawerRef.current
        ?.querySelector<HTMLElement>("[data-autofocus]")
        ?.focus({ preventScroll: true });
    } else if (wasExiting.current) {
      pane
        ?.querySelector<HTMLButtonElement>(".appointment-overlay__footer-btn--exit")
        ?.focus({ preventScroll: true });
    }
    wasExiting.current = isExiting;
  }, [isChoosingJourneyStep, isExiting, exitOutcome, cancellationReasons]);

  const checklistTitleRef = useRef<HTMLSpanElement>(null);
  const wasVerified = useRef(patientVerified);
  useEffect(() => {
    if (patientVerified && !wasVerified.current) {
      checklistTitleRef.current?.focus({ preventScroll: true });
    }
    wasVerified.current = patientVerified;
  }, [patientVerified]);

  // The ring counts both lists: the checklist and the forms of the journey
  // step that's up next.
  const journeyForms = useMemo(
    () => (showJourney ? upNext?.forms ?? [] : []),
    [showJourney, upNext]
  );
  // Remember how many rows the loaded checklist shows (the ring's total).
  const rowCount = tasks.length + journeyForms.length;
  useEffect(() => {
    if (!loading) onChecklistRowCount?.(rowCount);
  }, [loading, rowCount, onChecklistRowCount]);

  const { completed, total, remaining, percent } = useMemo(() => {
    const done =
      tasks.filter((task) => task.status === AppointmentTaskStatus.COMPLETED)
        .length +
      journeyForms.filter((form) => form.status === "completed").length;
    const all = tasks.length + journeyForms.length;
    return {
      completed: done,
      total: all,
      remaining: all - done,
      percent: all ? Math.round((done / all) * 100) : 0,
    };
  }, [tasks, journeyForms]);

  return (
    <section
      ref={cardRef}
      className={[
        "appointment-overlay",
        collapsed ? "appointment-overlay--collapsed" : "",
        side === "left" ? "appointment-overlay--left" : "",
        dragging ? "appointment-overlay--dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Appointment in progress"
    >
      {/*
        Identity down the left -- status line, patient name, then how to reach them --
        with the window controls top-right and a progress ring reading the visit checklist.
      */}
      <div className="appointment-overlay__header">
        <div className="appointment-overlay__eyebrow">
          <span className="appointment-overlay__dot" aria-hidden="true" />
          {detailsPath ? (
            <Link
              to={detailsPath}
              className="appointment-overlay__eyebrow-link"
              title="Go to appointment details"
            >
              Appointment in progress
              <span className="material-symbols-outlined" aria-hidden="true">
                north_east
              </span>
            </Link>
          ) : (
            "Appointment in progress"
          )}
          {timeLabel && (
            <span className="appointment-overlay__elapsed">· [{timeLabel}]</span>
          )}
        </div>

        <div className="appointment-overlay__header-controls">
          <button
            type="button"
            className="appointment-overlay__grip"
            aria-label={`Move to the ${
              side === "left" ? "right" : "left"
            } side. Drag, or use the arrow keys.`}
            title="Drag to move"
            {...gripProps}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              drag_indicator
            </span>
          </button>
          <button
            type="button"
            className="appointment-overlay__icon-btn"
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand visit checklist" : "Minimize visit checklist"}
            onClick={onToggleCollapsed}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {collapsed ? "expand_more" : "expand_less"}
            </span>
          </button>
        </div>

        <div className="appointment-overlay__identity">
          {patientPath ? (
            <Link
              to={patientPath}
              className="appointment-overlay__patient"
              title={`Go to ${patientName}'s profile`}
            >
              {patientName}
            </Link>
          ) : (
            <p className="appointment-overlay__patient" title={patientName}>
              {patientName}
            </p>
          )}

          {connectivity && (
            <AppointmentOverlayConnectivity
              connectivity={connectivity}
              appointmentId={appointmentId}
              onError={onConnectivityError}
            />
          )}
        </div>

        {/*
          The ring is the checklist's only progress indicator -- it stays visible when
          the card is minimized. Count changes are announced via the live region.
        */}
        <span className="appointment-overlay__sr-only" aria-live="polite">
          {!patientVerified
            ? "Verify the patient to see the visit checklist"
            : loading
            ? "Loading the visit checklist"
            : `${remaining} ${remaining === 1 ? "item" : "items"} left`}
        </span>
        <div
          className="appointment-overlay__tasks-meter"
          role="progressbar"
          aria-label="Visit checklist progress"
          aria-valuenow={completed}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuetext={loading ? "Loading" : `${completed} of ${total} complete`}
          aria-busy={loading || undefined}
        >
          <span className="appointment-overlay__ring" aria-hidden="true">
            <svg viewBox="0 0 44 44" aria-hidden="true">
              <circle className="appointment-overlay__ring-track" cx="22" cy="22" r="19" />
              <circle
                className="appointment-overlay__ring-fill"
                cx="22"
                cy="22"
                r="19"
                style={{
                  // 2πr, with the gap sized to the share still outstanding
                  strokeDasharray: 2 * Math.PI * 19,
                  strokeDashoffset: (2 * Math.PI * 19 * (100 - percent)) / 100,
                }}
              />
            </svg>
            <span className="appointment-overlay__ring-count" aria-hidden="true">
              {loading ? "–" : remaining}
            </span>
          </span>
          <span className="appointment-overlay__ring-label" aria-hidden="true">
            items
            <br />
            left
          </span>
        </div>
      </div>

      <div className="appointment-overlay__collapsible">
        <div>
          <div ref={panesRef} className="appointment-overlay__panes">
          <div ref={paneRef} aria-hidden={bodyCovered || undefined}>
          {!patientVerified ? (
            <AppointmentOverlayVerification
              patientInfoPath={patientInfoPath}
              onVerify={onVerifyPatient}
            />
          ) : (
          <div className="appointment-overlay__body">
            <div className="appointment-overlay__body-header">
              <span
                ref={checklistTitleRef}
                className="appointment-overlay__body-title"
                tabIndex={-1}
              >
                Visit checklist
              </span>
              {showJourney && !loading && (
                <JourneyStepSwitch onChange={() => setChoosingJourneyStep(true)} />
              )}
            </div>

            {loading ? (
              // Row-shaped placeholders while the checklist's first load is under
              // way, so the rows arrive together instead of popping in one by one.
              <ul
                className="appointment-overlay__tasks"
                aria-label="Visit checklist"
                aria-busy="true"
              >
                {/* As many rows as were showing before a refresh (2 for a new visit). */}
                {Array.from({ length: placeholderCount }, (_, index) => (
                  <li
                    key={index}
                    className="appointment-overlay__task appointment-overlay__task--skeleton"
                    aria-hidden="true"
                  >
                    {/* Built from a real row's parts, so it's exactly a row's height. */}
                    <span className="appointment-overlay__task-indicator appointment-overlay__skeleton" />
                    <div className="appointment-overlay__task-text">
                      <div className="appointment-overlay__task-title">
                        <span className="appointment-overlay__skeleton appointment-overlay__skeleton--line appointment-overlay__skeleton--title">
                          {"\u00a0"}
                        </span>
                      </div>
                      <div className="appointment-overlay__task-subtitle">
                        <span className="appointment-overlay__skeleton appointment-overlay__skeleton--line appointment-overlay__skeleton--subtitle">
                          {"\u00a0"}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <ul
                className="appointment-overlay__tasks appointment-overlay__tasks--loaded"
                aria-label="Visit checklist"
              >
                <JourneyFormRows forms={journeyForms} onOpen={onOpenJourneyForm} />
                {tasks.map((task) => (
                  <AppointmentOverlayTaskRow
                    key={task.id}
                    task={task}
                    onAction={onTaskAction}
                  />
                ))}
              </ul>
            )}
          </div>
          )}

          <div
            className="appointment-overlay__footer"
            onKeyDown={(event) => {
              if (
                event.key === "Escape" &&
                (pendingOutcome === "completed" || failedOutcome) &&
                !finishing
              ) {
                onCancelFinish();
              }
            }}
          >
            {failedOutcome ? (
              <>
                <p
                  className="appointment-overlay__confirm-text"
                  role="alert"
                  id="appointment-overlay-failed"
                >
                  {failedOutcome === "exit"
                    ? "Couldn't exit the appointment."
                    : "Couldn't save the appointment's status."}{" "}
                  Close anyway to leave it in progress.
                </p>
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--compact"
                  onClick={onRetryOutcome}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--compact appointment-overlay__footer-btn--quiet-solid"
                  onClick={onForceClose}
                  aria-describedby="appointment-overlay-failed"
                  autoFocus
                >
                  Close anyway
                </button>
              </>
            ) : pendingOutcome === "completed" ? (
              <>
                <p
                  className="appointment-overlay__confirm-text"
                  role="alert"
                  id="appointment-overlay-confirm"
                >
                  Mark this appointment as completed? This cannot be undone.
                </p>
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--compact"
                  onClick={onCancelFinish}
                  disabled={finishing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--compact appointment-overlay__footer-btn--primary"
                  onClick={onConfirmFinish}
                  disabled={finishing}
                  aria-describedby="appointment-overlay-confirm"
                  autoFocus
                >
                  {finishing ? "Saving..." : "Yes, finish appointment"}
                </button>
              </>
            ) : (
              <>
                {/* Every way out other than finishing opens over the body. */}
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--quiet appointment-overlay__footer-btn--exit"
                  onClick={() => setChoosingExit(true)}
                >
                  Exit appointment
                </button>
                <button
                  type="button"
                  className="appointment-overlay__footer-btn appointment-overlay__footer-btn--primary"
                  onClick={onFinish}
                  disabled={!patientVerified}
                  title={patientVerified ? undefined : "Verify the patient first"}
                >
                  Finish appointment
                </button>
              </>
            )}
          </div>
          </div>
          {showJourney && journey && (
            <div
              ref={drawerRef}
              className={`appointment-overlay__drawer${
                isChoosingJourneyStep ? " appointment-overlay__drawer--open" : ""
              }`}
              aria-hidden={!isChoosingJourneyStep}
            >
              <div className="appointment-overlay__body">
                <JourneyStepChooser
                  steps={journey.steps}
                  extras={journey.extras}
                  upNextId={upNext?.id ?? null}
                  onChoose={(step) => {
                    choose(step);
                    setChoosingJourneyStep(false);
                  }}
                  onCancel={() => setChoosingJourneyStep(false)}
                />
              </div>
            </div>
          )}
          <div
            ref={exitDrawerRef}
            className={`appointment-overlay__drawer${
              isExiting ? " appointment-overlay__drawer--open" : ""
            }`}
            aria-hidden={!isExiting}
          >
            <div className="appointment-overlay__body">
              <AppointmentOverlayExitChooser
                outcome={exitOutcome}
                finishing={finishing}
                cancellationReasons={cancellationReasons}
                cancelReason={cancelReason}
                onCancelReasonChange={onCancelReasonChange}
                onChoose={(outcome) => {
                  setChoosingExit(false);
                  if (outcome === "exit") onExit();
                  else if (outcome === "no-show") onNoShow();
                  else onCancelAppointment();
                }}
                onBack={() => {
                  if (exitOutcome) {
                    onCancelFinish();
                    setChoosingExit(true);
                  } else {
                    setChoosingExit(false);
                  }
                }}
                onConfirm={onConfirmFinish}
              />
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Grows the card while the step chooser is open, so every step shows without
 * scrolling when there's room: the panes' min-height eases from the body's
 * height to the chooser's full height (capped to what fits on screen under
 * the card's header), and back down as the drawer closes. The card is pinned
 * to the bottom of the screen, so it grows upward.
 *
 * The chooser fills the panes and fades in, so the card's growth is the only
 * motion: the list simply gets room as the card opens up.
 */
const fitPanesToDrawer = (
  panes: HTMLDivElement | null,
  pane: HTMLDivElement | null,
  drawer: HTMLDivElement | null,
  open: boolean
) => {
  if (!panes || !pane || !drawer) return;
  const bodyHeight = pane.offsetHeight;
  if (!open) {
    // Ease back to the body's height, then let it size itself again. A
    // timer rather than transitionend: the drawer's own fade bubbles one up,
    // and with reduced motion none fires at all.
    panes.style.minHeight = `${bodyHeight}px`;
    window.setTimeout(() => {
      if (!drawer.classList.contains("appointment-overlay__drawer--open")) {
        panes.style.minHeight = "";
      }
    }, 550);
    return;
  }
  const list = drawer.querySelector<HTMLElement>(".appointment-overlay__choices");
  const content = drawer.firstElementChild as HTMLElement | null;
  if (!content) return;
  // The chooser's natural height: what's around the list, plus the whole list.
  const needed = list
    ? content.offsetHeight - list.clientHeight + list.scrollHeight
    : content.scrollHeight;
  const card = panes.closest<HTMLElement>(".appointment-overlay");
  const header = card?.querySelector<HTMLElement>(".appointment-overlay__header");
  const room = window.innerHeight - 32 - (header?.offsetHeight ?? 0);
  const target = Math.max(bodyHeight, Math.min(needed, room));
  // Start from the current height so the growth eases from where it is.
  panes.style.minHeight = `${bodyHeight}px`;
  void panes.offsetHeight;
  panes.style.minHeight = `${target}px`;
};

export default React.memo(AppointmentOverlayCard);
